import { mockApiService } from './mockApiService'

export interface WalletType {
  id: string
  name: string
  icon: string
  description: string
  isAvailable: boolean
  supportedNetworks: string[]
}

export interface NetworkInfo {
  id: string
  name: string
  chainId: number
  rpcUrl: string
  blockExplorer: string
  nativeCurrency: {
    name: string
    symbol: string
    decimals: number
  }
}

export interface WalletConnectionState {
  isConnected: boolean
  walletType: string | null
  walletAddress: string | null
  networkId: string | null
  networkName: string | null
  chainId: number | null
  isConnecting: boolean
  error: string | null
  errorCode: string | null
  lastConnected: Date | null
  retryCount: number
  canRetry: boolean
}

export interface WalletConnectionResult {
  success: boolean
  walletAddress?: string
  networkId?: string
  chainId?: number
  error?: string
  errorCode?: string
  canRetry?: boolean
  retryCount?: number
}

export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

export class WalletConnectionManager {
  private state: WalletConnectionState = {
    isConnected: false,
    walletType: null,
    walletAddress: null,
    networkId: null,
    networkName: null,
    chainId: null,
    isConnecting: false,
    error: null,
    errorCode: null,
    lastConnected: null,
    retryCount: 0,
    canRetry: true
  }

  private listeners: Array<(state: WalletConnectionState) => void> = []

  constructor() {
    // Restore connection state on initialization
    this.restoreConnectionState()
  }

  // Supported wallet types
  private readonly supportedWallets: WalletType[] = [
    {
      id: 'metamask',
      name: 'MetaMask',
      icon: '🦊',
      description: 'Connect using MetaMask browser extension',
      isAvailable: true,
      supportedNetworks: ['ethereum', 'polygon', 'bsc']
    },
    {
      id: 'walletconnect',
      name: 'WalletConnect',
      icon: '🔗',
      description: 'Connect using WalletConnect protocol',
      isAvailable: true,
      supportedNetworks: ['ethereum', 'polygon', 'bsc', 'arbitrum']
    },
    {
      id: 'coinbase',
      name: 'Coinbase Wallet',
      icon: '🔵',
      description: 'Connect using Coinbase Wallet',
      isAvailable: true,
      supportedNetworks: ['ethereum', 'polygon', 'bsc']
    },
    {
      id: 'trust',
      name: 'Trust Wallet',
      icon: '🛡️',
      description: 'Connect using Trust Wallet',
      isAvailable: true,
      supportedNetworks: ['ethereum', 'polygon', 'bsc', 'tron']
    }
  ]

  // Supported networks
  private readonly supportedNetworks: NetworkInfo[] = [
    {
      id: 'ethereum',
      name: 'Ethereum Mainnet',
      chainId: 1,
      rpcUrl: 'https://mainnet.infura.io/v3/',
      blockExplorer: 'https://etherscan.io',
      nativeCurrency: { name: 'Ethereum', symbol: 'ETH', decimals: 18 }
    },
    {
      id: 'polygon',
      name: 'Polygon',
      chainId: 137,
      rpcUrl: 'https://polygon-rpc.com',
      blockExplorer: 'https://polygonscan.com',
      nativeCurrency: { name: 'Polygon', symbol: 'MATIC', decimals: 18 }
    },
    {
      id: 'bsc',
      name: 'BNB Smart Chain',
      chainId: 56,
      rpcUrl: 'https://bsc-dataseed.binance.org',
      blockExplorer: 'https://bscscan.com',
      nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 }
    },
    {
      id: 'tron',
      name: 'Tron Network',
      chainId: 728126428,
      rpcUrl: 'https://api.trongrid.io',
      blockExplorer: 'https://tronscan.org',
      nativeCurrency: { name: 'Tron', symbol: 'TRX', decimals: 6 }
    },
    {
      id: 'arbitrum',
      name: 'Arbitrum One',
      chainId: 42161,
      rpcUrl: 'https://arb1.arbitrum.io/rpc',
      blockExplorer: 'https://arbiscan.io',
      nativeCurrency: { name: 'Ethereum', symbol: 'ETH', decimals: 18 }
    }
  ]

  /**
   * Get current connection state
   */
  getState(): WalletConnectionState {
    return { ...this.state }
  }

  /**
   * Get supported wallet types
   */
  getSupportedWallets(): WalletType[] {
    return [...this.supportedWallets]
  }

  /**
   * Get supported networks
   */
  getSupportedNetworks(): NetworkInfo[] {
    return [...this.supportedNetworks]
  }

  /**
   * Get networks supported by a specific wallet type
   */
  getWalletSupportedNetworks(walletType: string): NetworkInfo[] {
    const wallet = this.supportedWallets.find(w => w.id === walletType)
    if (!wallet) return []

    return this.supportedNetworks.filter(network => 
      wallet.supportedNetworks.includes(network.id)
    )
  }

  /**
   * Validate wallet type selection
   */
  validateWalletType(walletType: string): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    if (!walletType) {
      errors.push('Please select a wallet type')
      return { isValid: false, errors, warnings }
    }

    const wallet = this.supportedWallets.find(w => w.id === walletType)
    if (!wallet) {
      errors.push('Selected wallet type is not supported')
      return { isValid: false, errors, warnings }
    }

    if (!wallet.isAvailable) {
      errors.push(`${wallet.name} is currently not available`)
      return { isValid: false, errors, warnings }
    }

    return { isValid: true, errors, warnings }
  }

  /**
   * Validate network compatibility
   */
  validateNetworkCompatibility(walletType: string, networkId: string): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    if (!walletType || !networkId) {
      errors.push('Both wallet type and network must be selected')
      return { isValid: false, errors, warnings }
    }

    const wallet = this.supportedWallets.find(w => w.id === walletType)
    const network = this.supportedNetworks.find(n => n.id === networkId)

    if (!wallet) {
      errors.push('Invalid wallet type selected')
      return { isValid: false, errors, warnings }
    }

    if (!network) {
      errors.push('Invalid network selected')
      return { isValid: false, errors, warnings }
    }

    if (!wallet.supportedNetworks.includes(networkId)) {
      errors.push(`${wallet.name} does not support ${network.name}`)
      return { isValid: false, errors, warnings }
    }

    return { isValid: true, errors, warnings }
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: (state: WalletConnectionState) => void): () => void {
    this.listeners.push(listener)
    return () => {
      const index = this.listeners.indexOf(listener)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.getState()))
  }

  /**
   * Update connection state
   */
  private updateState(updates: Partial<WalletConnectionState>): void {
    this.state = { ...this.state, ...updates }
    this.notifyListeners()
  }

  /**
   * Connect to wallet with realistic delays and mock behavior
   */
  async connectWallet(walletType: string, networkId: string, isRetry: boolean = false): Promise<WalletConnectionResult> {
    // Validate inputs
    const walletValidation = this.validateWalletType(walletType)
    if (!walletValidation.isValid) {
      return {
        success: false,
        error: walletValidation.errors[0],
        errorCode: 'INVALID_WALLET_TYPE'
      }
    }

    const networkValidation = this.validateNetworkCompatibility(walletType, networkId)
    if (!networkValidation.isValid) {
      return {
        success: false,
        error: networkValidation.errors[0],
        errorCode: 'NETWORK_INCOMPATIBLE'
      }
    }

    // Update retry count if this is a retry
    if (isRetry) {
      this.updateState({
        retryCount: this.state.retryCount + 1
      })
    } else {
      this.updateState({
        retryCount: 0
      })
    }

    // Set connecting state
    this.updateState({
      isConnecting: true,
      error: null,
      errorCode: null
    })

    try {
      // Simulate realistic connection delay (500-2000ms)
      const delay = Math.random() * 1500 + 500
      await new Promise(resolve => setTimeout(resolve, delay))

      // Simulate various connection failure scenarios (10% total chance)
      const failureRandom = Math.random()
      if (failureRandom < 0.03) {
        throw new Error('User rejected the connection request')
      } else if (failureRandom < 0.05) {
        throw new Error('Wallet is locked')
      } else if (failureRandom < 0.07) {
        throw new Error('Network error occurred')
      } else if (failureRandom < 0.09) {
        throw new Error('Connection timed out')
      } else if (failureRandom < 0.1) {
        throw new Error('Wallet extension not found')
      }

      // Generate mock wallet address
      const walletAddress = this.generateMockWalletAddress(walletType, networkId)
      const network = this.supportedNetworks.find(n => n.id === networkId)!

      // Update state with successful connection
      this.updateState({
        isConnected: true,
        isConnecting: false,
        walletType,
        walletAddress,
        networkId,
        networkName: network.name,
        chainId: network.chainId,
        error: null,
        errorCode: null,
        lastConnected: new Date(),
        retryCount: 0,
        canRetry: true
      })

      // Save connection state for persistence
      this.saveConnectionState()

      return {
        success: true,
        walletAddress,
        networkId,
        chainId: network.chainId,
        retryCount: this.state.retryCount
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Connection failed'
      let errorCode = 'CONNECTION_FAILED'
      
      // Map error messages to specific error codes
      if (errorMessage.includes('rejected')) {
        errorCode = 'USER_REJECTED'
      } else if (errorMessage.includes('locked')) {
        errorCode = 'WALLET_LOCKED'
      } else if (errorMessage.includes('Network error')) {
        errorCode = 'NETWORK_ERROR'
      } else if (errorMessage.includes('timed out')) {
        errorCode = 'TIMEOUT'
      } else if (errorMessage.includes('not found')) {
        errorCode = 'WALLET_NOT_INSTALLED'
      }

      // Determine if retry is possible (max 3 retries)
      const canRetry = this.state.retryCount < 3 && this.isRetryableError(errorCode)
      
      this.updateState({
        isConnecting: false,
        error: errorMessage,
        errorCode,
        canRetry
      })

      return {
        success: false,
        error: errorMessage,
        errorCode,
        canRetry,
        retryCount: this.state.retryCount
      }
    }
  }

  /**
   * Disconnect wallet
   */
  async disconnectWallet(): Promise<void> {
    // Simulate disconnect delay
    await new Promise(resolve => setTimeout(resolve, 200))

    // Clear saved state
    this.clearSavedState()

    this.updateState({
      isConnected: false,
      walletType: null,
      walletAddress: null,
      networkId: null,
      networkName: null,
      chainId: null,
      isConnecting: false,
      error: null,
      errorCode: null,
      lastConnected: null,
      retryCount: 0,
      canRetry: true
    })
  }

  /**
   * Check if wallet is connected and network is correct
   */
  async checkConnection(): Promise<boolean> {
    // In a real implementation, this would check the actual wallet connection
    // For demo purposes, we'll simulate this check
    if (!this.state.isConnected) return false

    // Simulate network check delay
    await new Promise(resolve => setTimeout(resolve, 100))

    // Simulate occasional network changes (5% chance)
    if (Math.random() < 0.05) {
      this.updateState({
        error: 'Network changed. Please reconnect your wallet.'
      })
      return false
    }

    return true
  }

  /**
   * Generate mock wallet address based on wallet type and network
   */
  private generateMockWalletAddress(walletType: string, networkId?: string): string {
    // For Tron network, generate Tron-style address regardless of wallet
    if (networkId === 'tron') {
      return 'T' + Array.from({ length: 33 }, () => 
        '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'[
          Math.floor(Math.random() * 58)
        ]
      ).join('')
    } else {
      // Ethereum-style address for all other networks
      return '0x' + Array.from({ length: 40 }, () => 
        '0123456789abcdef'[Math.floor(Math.random() * 16)]
      ).join('')
    }
  }

  /**
   * Get connection error with recovery guidance
   */
  getConnectionErrorGuidance(errorCode?: string): string {
    switch (errorCode) {
      case 'INVALID_WALLET_TYPE':
        return 'Please select a supported wallet type from the list above.'
      case 'NETWORK_INCOMPATIBLE':
        return 'The selected wallet does not support this network. Please choose a compatible network from the dropdown.'
      case 'CONNECTION_FAILED':
        return 'Connection failed. Please ensure your wallet is unlocked and try again.'
      case 'USER_REJECTED':
        return 'Connection was cancelled. Please try again and approve the connection request in your wallet.'
      case 'NETWORK_ERROR':
        return 'Network error occurred. Please check your internet connection and try again.'
      case 'WALLET_NOT_INSTALLED':
        return 'Wallet extension not found. Please install the wallet extension and refresh the page.'
      case 'WALLET_LOCKED':
        return 'Wallet is locked. Please unlock your wallet and try again.'
      case 'WRONG_NETWORK':
        return 'Wrong network detected. Please switch to the correct network in your wallet.'
      case 'INSUFFICIENT_PERMISSIONS':
        return 'Insufficient permissions. Please grant the necessary permissions in your wallet.'
      case 'TIMEOUT':
        return 'Connection timed out. Please try again or check your wallet settings.'
      default:
        return 'An unexpected error occurred. Please refresh the page and try again.'
    }
  }

  /**
   * Get detailed error recovery steps
   */
  getDetailedErrorRecovery(errorCode?: string): string[] {
    switch (errorCode) {
      case 'INVALID_WALLET_TYPE':
        return [
          'Select a wallet type from the dropdown menu',
          'Ensure the wallet is supported by our platform',
          'Contact support if your wallet should be supported'
        ]
      case 'NETWORK_INCOMPATIBLE':
        return [
          'Check which networks your wallet supports',
          'Select a compatible network from the dropdown',
          'Consider using a different wallet that supports this network'
        ]
      case 'CONNECTION_FAILED':
        return [
          'Unlock your wallet if it\'s locked',
          'Refresh the page and try again',
          'Check if your wallet extension is enabled',
          'Try connecting with a different wallet'
        ]
      case 'USER_REJECTED':
        return [
          'Click "Connect" again to retry',
          'Approve the connection request in your wallet popup',
          'Check if popup blockers are preventing the wallet popup'
        ]
      case 'NETWORK_ERROR':
        return [
          'Check your internet connection',
          'Try refreshing the page',
          'Wait a moment and try again',
          'Contact support if the problem persists'
        ]
      case 'WALLET_NOT_INSTALLED':
        return [
          'Install the wallet browser extension',
          'Refresh the page after installation',
          'Make sure the extension is enabled',
          'Try using a different supported wallet'
        ]
      case 'WALLET_LOCKED':
        return [
          'Open your wallet extension',
          'Enter your password to unlock',
          'Return to this page and try connecting again'
        ]
      case 'WRONG_NETWORK':
        return [
          'Open your wallet extension',
          'Switch to the correct network',
          'Return to this page and try connecting again'
        ]
      case 'TIMEOUT':
        return [
          'Try connecting again',
          'Check your internet connection',
          'Make sure your wallet is responsive',
          'Consider restarting your browser'
        ]
      default:
        return [
          'Refresh the page and try again',
          'Clear your browser cache',
          'Try using a different browser',
          'Contact support if the problem persists'
        ]
    }
  }

  /**
   * Determine if an error is retryable
   */
  private isRetryableError(errorCode: string): boolean {
    const retryableErrors = [
      'CONNECTION_FAILED',
      'NETWORK_ERROR',
      'TIMEOUT',
      'WALLET_LOCKED'
    ]
    return retryableErrors.includes(errorCode)
  }

  /**
   * Retry the last connection attempt
   */
  async retryConnection(): Promise<WalletConnectionResult | null> {
    if (!this.state.canRetry || !this.state.walletType) {
      return null
    }

    // Use the last attempted connection parameters
    const lastWalletType = this.state.walletType || ''
    const lastNetworkId = this.state.networkId || 'ethereum'

    return this.connectWallet(lastWalletType, lastNetworkId, true)
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.updateState({
      error: null,
      errorCode: null,
      canRetry: true
    })
  }

  /**
   * Save connection state to localStorage
   */
  private saveConnectionState(): void {
    try {
      const stateToSave = {
        walletType: this.state.walletType,
        networkId: this.state.networkId,
        lastConnected: this.state.lastConnected?.toISOString()
      }
      localStorage.setItem('walletConnectionState', JSON.stringify(stateToSave))
    } catch (error) {
      // Ignore localStorage errors
    }
  }

  /**
   * Restore connection state from localStorage
   */
  private restoreConnectionState(): void {
    try {
      const savedState = localStorage.getItem('walletConnectionState')
      if (savedState) {
        const parsed = JSON.parse(savedState)
        // Only restore non-sensitive state information
        this.updateState({
          walletType: parsed.walletType,
          networkId: parsed.networkId,
          lastConnected: parsed.lastConnected ? new Date(parsed.lastConnected) : null
        })
      }
    } catch (error) {
      // Ignore localStorage errors
    }
  }

  /**
   * Clear saved connection state
   */
  private clearSavedState(): void {
    try {
      localStorage.removeItem('walletConnectionState')
    } catch (error) {
      // Ignore localStorage errors
    }
  }

  /**
   * Reset connection state (useful for testing)
   */
  reset(): void {
    this.state = {
      isConnected: false,
      walletType: null,
      walletAddress: null,
      networkId: null,
      networkName: null,
      chainId: null,
      isConnecting: false,
      error: null,
      errorCode: null,
      lastConnected: null,
      retryCount: 0,
      canRetry: true
    }
    this.notifyListeners()
  }
}

// Export singleton instance
export const walletConnectionManager = new WalletConnectionManager()